/**
 * What the live charging screen should say about the vehicle's draw.
 *
 * ONE derivation, shared by the banner and the page heading, so they can
 * never disagree ("Charging in progress" above "Charging paused"). It adds
 * no detection of its own: it reads the authoritative session state --
 *
 *   • `live.chargingActivity` — the server's compound field
 *     (CHARGING / PAUSED_BY_VEHICLE / CHARGE_COMPLETE), seeded by the session
 *     DTO so it survives a cold reload;
 *   • `live.batteryFullSince` / `live.isIdle` — the same backend detector's
 *     SSE signals (SESSION_BATTERY_LIKELY_FULL / SESSION_IDLE_CHANGED);
 *
 * and applies the banner's existing guards unchanged: a stalled meter stream
 * wins, and a session that is observably progressing (energy rose within
 * the last 90 s, or instantaneous power > 0.1 kW) is never shown as paused
 * even if a backend flag is momentarily stale.
 *
 * Returns { kind, ... } where kind is one of:
 *   'stalled'      no MeterValues for > 10 min
 *   'full'         battery likely full (CHARGE_COMPLETE)       → paused
 *   'idle_long'    idle > 30 min, legacy fallback banner        → paused
 *   'paused'       paused by vehicle (idle, not yet full)       → paused
 *   'charging'     none of the above (incl. progressing overrides)
 * plus `suppressedBy` when a paused/full signal was overridden by progress.
 */

export const IDLE_BANNER_GRACE_MS = 90 * 1000;
const STALLED_MS = 10 * 60 * 1000;
const LEGACY_IDLE_MS = 30 * 60 * 1000;

export const PAUSED_KINDS = new Set(['full', 'idle_long', 'paused']);

export function deriveChargingPresentation(live, now = Date.now()) {
  const l = live || {};
  const idleSince = l.idleSince;
  const lastReadingAt = l.lastReadingAt;

  const idleDurationMs = idleSince ? Math.max(0, now - new Date(idleSince).getTime()) : 0;
  const stalenessMs = lastReadingAt ? Math.max(0, now - new Date(lastReadingAt).getTime()) : 0;
  if (lastReadingAt && stalenessMs > STALLED_MS) return { kind: 'stalled' };

  const lastEnergyIncreaseAt = l.lastEnergyIncreaseAt;
  const energyIncreasedRecently = !!lastEnergyIncreaseAt
    && (now - new Date(lastEnergyIncreaseAt).getTime()) < IDLE_BANNER_GRACE_MS;
  const drawingPowerNow = (l.powerKW || 0) > 0.1;
  const sessionProgressing = energyIncreasedRecently || drawingPowerNow;
  const suppressedBy = energyIncreasedRecently ? 'energy_increased_recently' : 'power_above_threshold';

  const activity = l.chargingActivity;
  const isComplete = activity === 'CHARGE_COMPLETE' || !!l.batteryFullSince;
  if (isComplete) {
    return sessionProgressing ? { kind: 'charging', suppressedBy, suppressed: 'full' } : { kind: 'full' };
  }

  const isPaused = activity === 'PAUSED_BY_VEHICLE' || !!l.isIdle;
  if (!isPaused) return { kind: 'charging' };
  if (sessionProgressing) return { kind: 'charging', suppressedBy, suppressed: 'paused' };
  if (idleDurationMs >= LEGACY_IDLE_MS) return { kind: 'idle_long' };
  return { kind: 'paused' };
}
